var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { isOk, voidResult } from "healthex-common/result";
import { t } from "i18next";
import { computed, observable, reaction } from "mobx";
import { Persistence } from "../Infra/Persistence";
import { willNeverHappen } from "healthex-common/utils";
import { AxiosError } from "axios";
import { UserType } from "healthex-common/user.dtos";
import { NamedRoutes } from "../UI/NamedRoutes";
export var SmartAuthProvider;
(function (SmartAuthProvider) {
    SmartAuthProvider["EPIC"] = "EPIC";
    SmartAuthProvider["FASTEN"] = "FASTEN";
})(SmartAuthProvider || (SmartAuthProvider = {}));
export var SmartAuthType;
(function (SmartAuthType) {
    SmartAuthType["PROVIDER_AUTH"] = "PROVIDER_AUTH";
    SmartAuthType["PATIENT_AUTH"] = "PATIENT_AUTH";
    SmartAuthType["PATIENT_REAUTH"] = "PATIENT_REAUTH";
    SmartAuthType["PROXY_ACCESS_REAUTH"] = "PROXY_ACCESS_REAUTH";
})(SmartAuthType || (SmartAuthType = {}));
export var SmartAuthState;
(function (SmartAuthState) {
    SmartAuthState["INACTIVE"] = "INACTIVE";
    SmartAuthState["IN_PROGRESS"] = "IN_PROGRESS";
    SmartAuthState["LOGGING_IN"] = "LOGGING_IN";
    SmartAuthState["ERROR"] = "ERROR";
    SmartAuthState["COMPLETED"] = "COMPLETED";
})(SmartAuthState || (SmartAuthState = {}));
export var SmartAuthErrorReason;
(function (SmartAuthErrorReason) {
    SmartAuthErrorReason["LOGIN_WINDOW_CLOSED"] = "LOGIN_WINDOW_CLOSED";
    SmartAuthErrorReason["SMART_AUTH_GENERIC"] = "SMART_AUTH_GENERIC";
    SmartAuthErrorReason["SMART_AUTH_UNEXPECTED_CALLBACK"] = "SMART_AUTH_UNEXPECTED_CALLBACK";
    SmartAuthErrorReason["SMART_AUTH_INVALID_STATE_PARAM"] = "SMART_AUTH_INVALID_STATE_PARAM";
    SmartAuthErrorReason["CLEAR_AUTH_GENERIC"] = "CLEAR_AUTH_GENERIC";
    SmartAuthErrorReason["CLEAR_AUTH_UNEXPECTED_CALLBACK"] = "CLEAR_AUTH_UNEXPECTED_CALLBACK";
    SmartAuthErrorReason["CLEAR_AUTH_INVALID_STATE_PARAM"] = "CLEAR_AUTH_INVALID_STATE_PARAM";
    SmartAuthErrorReason["AUTH_FAILED"] = "AUTH_FAILED";
    SmartAuthErrorReason["PATIENT_NOT_FOUND"] = "PATIENT_NOT_FOUND";
    SmartAuthErrorReason["CLEAR_VALIDATION_FAILED"] = "CLEAR_VALIDATION_FAILED";
})(SmartAuthErrorReason || (SmartAuthErrorReason = {}));
export class SmartAuthManager {
    get hasCompletedAuth() {
        return this.state.state === SmartAuthState.COMPLETED || this.state.state === SmartAuthState.ERROR;
    }
    get hasPendingAuthDataToProcess() {
        var _a;
        return ((_a = this.getSmartAuthData()) === null || _a === void 0 ? void 0 : _a.provider) === SmartAuthProvider.FASTEN;
    }
    constructor(authService, authManager, persistence, environment, routingUIManager) {
        this.authService = authService;
        this.authManager = authManager;
        this.persistence = persistence;
        this.environment = environment;
        this.routingUIManager = routingUIManager;
        this.state = { state: SmartAuthState.INACTIVE };
        this.error = null;
        this.errorReason = null;
        this.reauthSuccess = null;
        this.reauthProxyAccess = null;
        reaction(() => this.routingUIManager.currentRoute, () => {
            var _a;
            const allowList = [NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName];
            if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.FASTEN_CALLBACK.routeName) {
                this.handleFastenCallback();
            }
            else if (this.routingUIManager.currentRoute && this.hasPendingAuthDataToProcess) {
                if (allowList.includes(this.routingUIManager.currentRoute.routeName)) {
                    return;
                }
                console.warn(`Still pending auth data, marking as error`);
                this.onError(null, SmartAuthErrorReason.SMART_AUTH_GENERIC);
            }
        }, { fireImmediately: true, name: "SmartAuthManager_routingUIManagerCurrentRoute" });
        window.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.resetAuthStateForFastenIfNeeded();
            }
        });
    }
    async redirectToProviderPortalForFastenAuth(publicId, endpointId, brandId, portalId, externalId, locationKey) {
        const smartAuthData = {
            provider: SmartAuthProvider.FASTEN,
            type: SmartAuthType.PATIENT_AUTH,
            state: crypto.randomUUID(),
            sandboxMode: this.authManager.isSandbox,
            publicId,
            endpointId,
            brandId,
            portalId,
            externalId,
            callbackHash: window.location.hash,
            locationKey: locationKey !== null && locationKey !== void 0 ? locationKey : undefined,
        };
        const url = await this.startSmartAuth(SmartAuthProvider.FASTEN, SmartAuthType.PATIENT_AUTH, this.getRedirectUri(SmartAuthProvider.FASTEN), undefined, undefined, this.authManager.isSandbox, smartAuthData);
        window.location.href = url;
    }
    async startSmartAuth(provider, type, redirectUrl, organizationId, launch, sandboxMode, smartAuthData) {
        this.reauthSuccess = null;
        this.reauthProxyAccess = null;
        this.state = {
            state: SmartAuthState.IN_PROGRESS,
            key: (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN ? smartAuthData.locationKey : undefined,
        };
        this.persistence.setSmartAuthData(null);
        this.onError(null, null, false);
        console.log(`Starting Smart Auth session, with the ${provider} provider`);
        switch (provider) {
            case SmartAuthProvider.EPIC: {
                if (!organizationId) {
                    throw new Error("No organization id for EPIC authentication");
                }
                const urlResponse = await this.authService.getSmartAuthUrl(organizationId, redirectUrl, launch, type === SmartAuthType.PROVIDER_AUTH ? UserType.HEALTHCARE_ORGANIZATION : UserType.PATIENT);
                this.persistence.setSmartAuthData({
                    provider,
                    type,
                    state: urlResponse.state,
                    organizationId,
                    isEHRLaunch: !!launch,
                    sandboxMode,
                });
                return urlResponse.url;
            }
            case SmartAuthProvider.FASTEN: {
                if (!smartAuthData || smartAuthData.provider !== SmartAuthProvider.FASTEN) {
                    throw new Error("Missing or invalid smartAuthData for Fasten authentication");
                }
                this.persistence.setSmartAuthData(smartAuthData);
                const params = new URLSearchParams({
                    public_id: smartAuthData.publicId,
                    endpoint_id: smartAuthData.endpointId,
                    brand_id: smartAuthData.brandId,
                    portal_id: smartAuthData.portalId,
                    redirect_uri: redirectUrl,
                    external_state: smartAuthData.state,
                    external_id: smartAuthData.externalId,
                });
                return `${this.environment.FASTEN_API_BASE_URL}/v1/bridge/connect?${params.toString()}`;
            }
            default:
                return willNeverHappen(provider);
        }
    }
    async onSmartAuthComplete(smartCode, state, redirectUri) {
        const smartAuthData = this.getSmartAuthData();
        if (!smartAuthData) {
            this.onError("Unexpected auth callback", SmartAuthErrorReason.SMART_AUTH_UNEXPECTED_CALLBACK, false);
            return;
        }
        const { type, state: persistedState, sandboxMode } = smartAuthData;
        if (persistedState !== state) {
            this.onError("Invalid state parameter returned by server", SmartAuthErrorReason.SMART_AUTH_INVALID_STATE_PARAM, false);
            return;
        }
        this.state = {
            state: SmartAuthState.LOGGING_IN,
            key: (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN ? smartAuthData.locationKey : undefined,
        };
        try {
            if (type === SmartAuthType.PATIENT_AUTH || type === SmartAuthType.PROVIDER_AUTH) {
                const result = await this.auth(smartAuthData, smartCode, redirectUri, sandboxMode);
                if (isOk(result)) {
                    this.state = { state: SmartAuthState.INACTIVE };
                }
                else {
                    if (result.error === "PATIENT_NOT_FOUND") {
                        this.onError(t("We couldn't find any information linking you to this healthcare provider.\n\nPlease double-check that you've selected the correct provider. If the issue continues, contact your healthcare provider for support."), SmartAuthErrorReason.PATIENT_NOT_FOUND);
                    }
                    else if (result.error === "CLEAR_VALIDATION_FAILED") {
                        this.onError(t("We could not validate your identity, please try again"), SmartAuthErrorReason.CLEAR_VALIDATION_FAILED);
                    }
                    else {
                        this.onError(t("Authentication failed"), SmartAuthErrorReason.AUTH_FAILED);
                    }
                }
            }
            else if (type === SmartAuthType.PATIENT_REAUTH || type === SmartAuthType.PROXY_ACCESS_REAUTH) {
                const result = await this.reauth(smartAuthData, smartCode, redirectUri, sandboxMode);
                if (isOk(result)) {
                    this.reauthSuccess = true;
                    if (type === SmartAuthType.PROXY_ACCESS_REAUTH && typeof result === "object" && "relatedPerson" in result) {
                        this.reauthProxyAccess = { ...result };
                    }
                    this.state = { state: SmartAuthState.INACTIVE };
                }
                else {
                    if (result.error === "CLEAR_VALIDATION_FAILED") {
                        this.onError(t("We could not validate your identity, please try again"), SmartAuthErrorReason.CLEAR_VALIDATION_FAILED);
                    }
                    else {
                        this.onError(t("Re-authentication failed"), SmartAuthErrorReason.AUTH_FAILED);
                    }
                }
            }
            else {
                this.onError(t("Unknown authentication type"), SmartAuthErrorReason.AUTH_FAILED);
            }
        }
        catch (e) {
            console.error(e);
            const err = typeof e === "object" && e !== null && "message" in e ? String(e.message) : String(e);
            this.onError(err, SmartAuthErrorReason.AUTH_FAILED);
        }
        finally {
            this.persistence.setSmartAuthData(null);
        }
    }
    async auth(smartAuthData, smartCode, redirectUri, sandboxMode) {
        switch (smartAuthData.provider) {
            case SmartAuthProvider.EPIC:
                if (!smartCode || !redirectUri) {
                    this.onError(t("SMART on FHIR authentication failed - no smart code"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                if (!smartAuthData.organizationId) {
                    this.onError(t("SMART on FHIR authentication failed - no org id"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                try {
                    return await this.authManager.authenticateBySmartCode(smartCode, smartAuthData.type === SmartAuthType.PROVIDER_AUTH ? UserType.HEALTHCARE_ORGANIZATION : UserType.PATIENT, smartAuthData.organizationId, redirectUri, sandboxMode);
                }
                catch (e) {
                    if (e instanceof AxiosError) {
                        this.onError(t("SMART on FHIR authentication failed - invalid smart code"), SmartAuthErrorReason.AUTH_FAILED);
                        return voidResult();
                    }
                    else {
                        throw e;
                    }
                }
            case SmartAuthProvider.FASTEN:
                return voidResult();
            default:
                this.onError(t("Unkown authentication provider"), SmartAuthErrorReason.AUTH_FAILED);
                return voidResult();
        }
    }
    async reauth(smartAuthData, smartCode, redirectUri, sandboxMode) {
        switch (smartAuthData.provider) {
            case SmartAuthProvider.EPIC:
                if (!smartCode || !redirectUri) {
                    this.onError(t("SMART on FHIR authentication failed - no smart code"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                if (!smartAuthData.organizationId) {
                    this.onError(t("SMART on FHIR authentication failed - no org id"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                return await this.authManager.reauthenticateBySmartCode(smartAuthData.type, smartCode, smartAuthData.organizationId, redirectUri, sandboxMode);
            default:
                this.onError(t("Unkown authentication provider in re-auth"), SmartAuthErrorReason.AUTH_FAILED);
                return voidResult();
        }
    }
    onError(error, reason, addErrorState = true) {
        this.error = error;
        if (addErrorState) {
            const smartAuthData = this.getSmartAuthData();
            this.state = {
                state: SmartAuthState.ERROR,
                key: (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN ? smartAuthData.locationKey : undefined,
                error: reason !== null && reason !== void 0 ? reason : undefined,
            };
        }
        this.errorReason = reason;
    }
    async onSmartAuthError(error, reason) {
        this.onError(error, reason !== null && reason !== void 0 ? reason : SmartAuthErrorReason.SMART_AUTH_GENERIC);
        this.persistence.setSmartAuthData(null);
    }
    getRedirectUri(provider) {
        let url;
        switch (provider) {
            case SmartAuthProvider.EPIC:
                url = `${window.location.origin}/#/smart-callback`;
                break;
            case SmartAuthProvider.FASTEN:
                url = `${window.location.origin}`;
                break;
            default:
                return willNeverHappen(provider);
        }
        return url;
    }
    static checkForFastenRedirectCode() {
        const queryParams = new URLSearchParams(window.location.search);
        const hasRedirectParams = ["endpoint_id", "brand_id", "portal_id", "connection_status"].every((p) => queryParams.has(p)) ||
            ["error", "error_description"].every((p) => queryParams.has(p));
        if (!hasRedirectParams)
            return false;
        const smartAuthData = new Persistence().getSmartAuthData();
        return (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN;
    }
    getQueryParamsFromHash() {
        let queryString = window.location.search;
        if (!queryString && window.location.hash.includes("?")) {
            const hashParts = window.location.hash.split("?");
            queryString = `?${hashParts[1]}`;
        }
        return new URLSearchParams(queryString);
    }
    handleFastenCallback() {
        const smartAuthData = this.getSmartAuthData();
        const queryParams = this.getQueryParamsFromHash();
        const error = queryParams.has("error");
        const isAuthorized = queryParams.get("connection_status") === "authorized";
        const isCallbackValid = (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN &&
            queryParams.get("external_state") === smartAuthData.state &&
            (error || isAuthorized);
        if (!isCallbackValid) {
            this.onError(null, SmartAuthErrorReason.SMART_AUTH_GENERIC);
            return;
        }
        this.persistence.setSmartAuthData({ ...smartAuthData, queryParams: queryParams.toString() });
        const locationKey = (smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN ? smartAuthData.locationKey : undefined;
        this.state = error
            ? { state: SmartAuthState.ERROR, key: locationKey, error: SmartAuthErrorReason.SMART_AUTH_GENERIC }
            : { state: SmartAuthState.COMPLETED, key: locationKey };
    }
    resetAuthStateForFastenIfNeeded() {
        const smartAuthData = this.getSmartAuthData();
        if ((smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN) {
            this.setSmartAuthData(null);
            this.resetAuth();
        }
    }
    resetAuth() {
        this.state = { state: SmartAuthState.INACTIVE };
        this.error = null;
        this.errorReason = null;
        this.reauthSuccess = null;
        this.reauthProxyAccess = null;
    }
    setSmartAuthData(smartAuthData) {
        this.persistence.setSmartAuthData(smartAuthData);
    }
    getSmartAuthData() {
        return this.persistence.getSmartAuthData();
    }
    getAndClearSmartAuthData() {
        const smartAuthData = this.getSmartAuthData();
        this.setSmartAuthData(null);
        return smartAuthData;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "state", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "error", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "errorReason", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "reauthSuccess", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "reauthProxyAccess", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], SmartAuthManager.prototype, "hasCompletedAuth", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], SmartAuthManager.prototype, "hasPendingAuthDataToProcess", null);
